import { FirebaseError } from "firebase/app";
import { createUserWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import { Schema } from "nutso";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { Form } from "../form/Form";
import { FormTextInput } from "../form/FormTextInput";
import { useValidation } from "../hooks/useValidation";
import { firebaseAuth } from "../services/firebaseAuth";
import { asyncOperation } from "../utils/asyncOperation";
import { toastStore } from "../utils/toastStore";
import { signInWithGooglePopup } from "../services/signInWithGooglePopup";
import { signInWithMicrosoftPopup } from "../services/signInWithMicrosoftPopup";

type SignupInfo = {
  email: string;
  password: string;
  confirmPassword: string;
};

const signupInfoSchema: Schema<SignupInfo> = {
  type: "object",
  properties: {
    email: {
      type: "string",
    },
    password: {
      type: "string",
    },
    confirmPassword: {
      type: "string",
      validationFn: (args) => {
        const form = args.parent;
        if (form.password != form.confirmPassword) return { errorMessage: "Passwords do not match" };
      },
    },
  },
};

type Props = {
  onChange: () => void;
};
export const SignupWidget = (props: Props) => {
  //
  const [info, setInfo] = useState<SignupInfo>({
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [busy, setBusy] = useState(false);
  const validation = useValidation(info, signupInfoSchema);

  const onSubmit = async () => {
    // create
    await asyncOperation({
      fn: () => createUserWithEmailAndPassword(firebaseAuth, info.email, info.password),
      loadingMessage: `Signing up..`,
      setBusyFlag: (busy) => setBusy(busy),
      onSuccess: (result) => {
        sendEmailVerification(result.user);
        toast.success("A verification email has been sent to your email. Please check your inbox to continue.");
      },
      onError: (e: any) => {
        if (e instanceof FirebaseError) {
          switch (e.code) {
            case "auth/email-already-in-use":
              toastStore.info("Email already exists. Login instead.");
              return;
          }
        }
      },
    });
  };

  const handleGoogleSignIn = async () => {
    await asyncOperation({
      fn: () => signInWithGooglePopup(),
      loadingMessage: `Logging in..`,
      setBusyFlag: (busy: boolean) => setBusy(busy),
      successMessage: "Successfully logged in",
    });
  };

  const handleMicrosoftSignIn = async () => {
    await asyncOperation({
      fn: () => signInWithMicrosoftPopup(),
      loadingMessage: `Logging in..`,
      setBusyFlag: (busy: boolean) => setBusy(busy),
      successMessage: "Successfully logged in",
    });
  };

  return (
    <div className="signup-widget">
      <div className="form-header">
        <div className="heading">Signup</div>
        <div className="sub-heading">Signup with your personal email to setup your organisation’s work email.</div>
      </div>
      <Form type="default" submit={{ label: "Signup", handler: onSubmit }} validationResult={validation} loading={busy}>
        <FormTextInput
          validation={validation.properties.email}
          label="Email"
          value={info.email}
          additionalClassNames="input-label"
          onChange={(value) => setInfo((prev) => ({ ...prev, email: value }))}
          type="email"
          dataCy="auth-email"
        />
        <FormTextInput
          validation={validation.properties.password}
          label="Password"
          value={info.password}
          additionalClassNames="input-label"
          onChange={(value) => setInfo((prev) => ({ ...prev, password: value }))}
          type="password"
          dataCy="auth-password"
        />
        <FormTextInput
          label="Confirm Password"
          value={info.confirmPassword}
          additionalClassNames="input-label"
          validation={validation.properties.confirmPassword}
          onChange={(value) => setInfo((prev) => ({ ...prev, confirmPassword: value }))}
          type="password"
          dataCy="auth-confirm-password"
        />
      </Form>
      <div className="form-actions">
        <div className="divider">
          <div className="rectangle"></div>
          <div className="or">or</div>
          <div className="rectangle"></div>
        </div>
        <button className="card-button" onClick={handleGoogleSignIn}>
          Signup with Google
        </button>
        <button className="card-button" onClick={handleMicrosoftSignIn}>
          Signup with Outlook
        </button>
        <div className="login-message">
          Already have an account?&nbsp;
          <span onClick={props.onChange}>
            <a href="#">Login</a>
          </span>
        </div>
      </div>
    </div>
  );
};
